import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { resolveAssetUrl } from '../utils/assetUrl';
import { uploadMedia } from '../services/uploadService';

function defaultAccept(kind) {
  return kind === 'document' ? '.pdf,application/pdf' : 'image/jpeg,image/png,image/webp,image/avif';
}

export default function MediaUploadField({
  label,
  value,
  onChange,
  kind = 'image',
  scope,
  slug,
  previousUrl,
  manualLabel,
  helperText,
  error,
  touched,
  requireSlug = false,
  uploadHandler,
  accept,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const resolvedValue = useMemo(() => resolveAssetUrl(value), [value]);

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please choose a file first.');
      return;
    }

    if (requireSlug && !slug) {
      setUploadError('Please enter the slug before uploading.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setSuccessMessage('');

    try {
      const handler = uploadHandler || ((params) => uploadMedia(params));
      const result = await handler({
        file: selectedFile,
        kind,
        scope,
        slug,
        previousUrl: previousUrl || value,
      });

      onChange(result.url);
      setSuccessMessage('File uploaded successfully.');
      setSelectedFile(null);
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Upload failed.';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>

      <TextField
        label={manualLabel || `${label} URL`}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        variant="outlined"
        margin="normal"
        fullWidth
        error={Boolean(touched && error)}
        helperText={(touched && error) || helperText}
      />

      {resolvedValue && kind === 'image' && (
        <Box mt={1} mb={2}>
          <img
            src={resolvedValue}
            alt={label}
            style={{ width: 160, maxHeight: 160, objectFit: 'cover', borderRadius: 10, border: '1px solid #E2E8F0' }}
          />
        </Box>
      )}

      {resolvedValue && kind === 'document' && (
        <Box mt={1} mb={2}>
          <Link href={resolvedValue} target="_blank" rel="noopener noreferrer">
            View current file
          </Link>
        </Box>
      )}

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" component="label" disabled={uploading}>
          Choose File
          <input
            hidden
            type="file"
            accept={accept || defaultAccept(kind)}
            onChange={(event) => {
              setSelectedFile(event.target.files?.[0] || null);
              setUploadError('');
              setSuccessMessage('');
            }}
          />
        </Button>

        <Typography variant="body2" color="text.secondary">
          {selectedFile ? selectedFile.name : 'No file selected'}
        </Typography>

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
        </Button>
      </Box>

      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}
    </Paper>
  );
}
